import React from 'react'
import PropTypes from 'prop-types'
import { AsyncPaginate } from 'react-select-async-paginate'

import { makeStyles, useTheme } from '@material-ui/core/styles'
import IconButton from '@material-ui/core/IconButton'
import HighlightOffIcon from '@material-ui/icons/HighlightOff'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import clsx from 'clsx'
import jsonApi from '../../utils/jsonApi'
import http from '../../utils/http'
import { isEqual } from 'lodash'

const useStyles = makeStyles(theme => ({
  root: {
    border: props => props.borderColor,
    backgroundColor: '#FFF',
    borderRadius: '8px',
    width: '100%',
    '& .select__input > input': {
      height: '38px',
      paddingBottom: 12
    },
    '& .select__menu': {
      zIndex: 1000
    },
    '& .select__multi-value': {
      marginBottom: '2px',
      marginTop: '2px'
    },
    '& .select__control, .select__value-container': {
      height: props => (!props.isMulti ? '48px' : 'auto'),
      minHeight: '48px',
      alignContent: 'center',
      paddingTop: 0,
      borderRadius: '8px !important',
      color: '#FFF'
    },
    '& .select__control': {
      border: 'none !important',
      borderRadius: '8px'
    },
    '& .select__placeholder': {
      color: '#ADB8CC',
      fontSize: '1rem',
      [theme.breakpoints.down('340')]: {
        fontSize: '0.8rem'
      }
    },
    '& .select__indicators': {
      height: 48
    },
    '& .select__single-value': {
      color: theme.palette.text.primary
    },
    '& .select__value-container > .select__multi-value': {
      justifyContent: 'space-around'
    },
    '& .select__value-container > .select__multi-value > .select__multi-value__label': {
      color: 'white'
    },
    '& .select__value-container > .select__multi-value:nth-of-type(3n-2)': {
      background: '#F4A77B',
      borderRadius: 8
    },
    '& .select__value-container > .select__multi-value:nth-of-type(3n-1)': {
      background: '#00ACDB',
      borderRadius: 8
    },
    '& .select__value-container > .select__multi-value:nth-of-type(3n)': {
      background: '#EF7B8E',
      borderRadius: 8
    }
  },
  error: {
    color: theme.palette.danger.main,
    fontSize: '0.8rem'
  },
  indicatorSelect: {
    display: 'flex',
    alignItems: 'center',
    '& > button.MuiIconButton-root': {
      padding: '7px'
    }
  },
  indicatorClear: {
    '& > button.MuiIconButton-root': {
      color: theme.palette.primary.dark,
      padding: '7px'
    }
  },
  arrowIcon: {
    transition: 'transform 0.3s ease',
    transformOrigin: 'center'
  },
  arrowOpen: {
    transform: 'rotate(180deg)'
  }
}))

const SelectAsyncPaginate = ({
  request,
  searchParam,
  placeholder,
  handleAsyncChange,
  defaultValue,
  isMulti,
  disabled,
  meta: { touched, error },
  input,
  touch,
  defaultOptions = true,
  rootStyle,
  optionsObject,
  ...rest
}) => {
  const [eventCreator, setEventCreator] = React.useState(false)
  const generalTheme = useTheme()
  const classes = useStyles({
    borderColor:
      touched && error
        ? `1px solid ${generalTheme.palette.danger.main} !important`
        : '1px solid #ADB8CC',
    isMulti
  })

  const cacheUniqsRef = React.useRef(rest.cacheUniqs)
  if (!isEqual(cacheUniqsRef.current, rest.cacheUniqs)) {
    cacheUniqsRef.current = rest.cacheUniqs
  }
  const stableCacheUniqs = cacheUniqsRef.current

  React.useEffect(() => {
    if (request.path === 'events/event_creators') {
      setEventCreator(true)
    }
  }, [])

  const loadOptions = async (search, loadedOptions = '', { page }) => {
    const hasSearchParam = searchParam ? { [searchParam]: search } : ''
    const hasComplementLabelParams = !!request.complementLabelParams
    const hasInclude =
      request.params && request.params.hasOwnProperty('include')
    const buildInclude = () => {
      const fromComplement =
        request.complementLabelParams?.includeOptions?.include
      const fromParams = request.params?.include

      if (fromComplement && fromParams) return `${fromComplement},${fromParams}`
      if (fromComplement) return fromComplement
      if (fromParams) return fromParams
      return undefined
    }

    const include = buildInclude()
    const requestParams = {
      ...hasSearchParam,
      ...request.params,
      ...(include ? { include } : {}),
      'page[number]': page
    }
    let response
    if (request.path !== 'events/event_creators') {
      response = await jsonApi
        .findAll(request.path, { params: requestParams })
        .then(res => res)
    } else {
      response = await http.get(request.path).then(res => res)
    }

    const options = response.data.map(data => {
      // Se tiver include, usa os dados do relacionamento como base
      const baseData = hasInclude ? data[request.params['include']] : data
      let complementLabel = baseData?.title || baseData?.name

      complementLabel = optionsObject
        ? optionsObject[complementLabel]
        : complementLabel

      if (hasComplementLabelParams) {
        complementLabel =
          complementLabel + request.complementLabelParams.sublabel(data)
      }

      return {
        label: complementLabel,
        value: baseData?.id
      }
    })
    // filter duplicate values
    const optionsFilter = options
      .filter(
        element => element.label !== undefined && element.value !== undefined
      )
      .filter(
        (element, index, self) =>
          index === self.findIndex(seg => seg.value === element.value)
      )

    const resp = {
      options: optionsFilter,
      additional: {
        page: page + 1
      }
    }

    if (eventCreator === false) {
      Object.assign(resp, { hasMore: response.data.length >= 1 })
    }

    return resp
  }

  const wrappedLoadOptions = (...args) => {
    return loadOptions(...args)
  }

  const ClearIndicator = ({ innerRef, innerProps }) => (
    <span ref={innerRef} {...innerProps} className={classes.indicatorClear}>
      <IconButton>
        <HighlightOffIcon />
      </IconButton>
    </span>
  )

  const DropdownIndicator = ({ innerRef, innerProps, selectProps }) => {
    const open = selectProps.menuIsOpen
    return (
      <span ref={innerRef} {...innerProps} className={classes.indicatorSelect}>
        <IconButton>
          <ExpandMoreIcon
            className={clsx(classes.arrowIcon, { [classes.arrowOpen]: open })}
          />
        </IconButton>
      </span>
    )
  }

  return (
    <div className={`${rootStyle}`}>
      <AsyncPaginate
        classNamePrefix='select'
        key={`${input.name}[${rest?.cacheUniqs}]`}
        debounceTimeout={500}
        cacheOptions
        onBlurResetsInput
        className={classes.root}
        defaultOptions={defaultOptions}
        onMouse
        isDisabled={disabled}
        isMulti={isMulti}
        isClearable
        closeMenuOnSelect={!isMulti}
        components={{
          IndicatorSeparator: () => null,
          ClearIndicator,
          DropdownIndicator
        }}
        loadingMessage={() => 'Carregando...'}
        noOptionsMessage={() => 'Não encontrado'}
        loadOptions={wrappedLoadOptions}
        minMenuHeight={50}
        onChange={handleAsyncChange}
        placeholder={placeholder}
        defaultValue={defaultValue}
        onFocus={e => {
          touch(input.name)
        }}
        name={input.name}
        {...rest}
        cacheUniqs={stableCacheUniqs}
        additional={{
          page: 1
        }}
      />
      {touched && error && <span className={classes.error}>{error}</span>}
    </div>
  )
}

SelectAsyncPaginate.propTypes = {
  request: PropTypes.object.isRequired,
  searchParam: PropTypes.string,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.any,
  handleAsyncChange: PropTypes.func,
  meta: PropTypes.object,
  input: PropTypes.object,
  touch: PropTypes.any.isRequired,
  borderColor: PropTypes.string,
  isMulti: PropTypes.bool,
  disabled: PropTypes.bool,
  defaultOptions: PropTypes.bool,
  rootStyle: PropTypes.object,
  optionsObject: PropTypes.object
}

export default SelectAsyncPaginate
